import type { ReactNode } from 'react';
import { Input } from '@/shared/ui/input';
import { Textarea } from '@/shared/ui/textarea';
import { Checkbox } from '@/shared/ui/checkbox';
import type { DocumentDraft } from '@/modules/agreements/platform/pages/document-create-page/types';

/**
 * Step 2 — the first published version's content.
 *
 * A version is IMMUTABLE once published: publishing a new version does not
 * change an existing one. That is why the content is authored here, in the
 * creation flow, rather than being editable afterwards — the operator is writing
 * the first version, not editing a draft.
 *
 * The server assigns the version number; the client never sends one.
 */
export function ContentStep(props: {
  draft: DocumentDraft;
  onChange: (patch: Partial<DocumentDraft>) => void;
}): ReactNode {
  const { draft, onChange } = props;
  const localeTouched = draft.locale.length > 0;
  const localeValid = /^[a-z]{2}(-[A-Z]{2})?$/.test(draft.locale);

  return (
    <div className="space-y-4">
      <Input
        label="Locale"
        value={draft.locale}
        onChange={(event) => onChange({ locale: event.target.value })}
        hint="e.g. en or en-IN"
        error={localeTouched && !localeValid ? 'Must match ^[a-z]{2}(-[A-Z]{2})?$' : undefined}
        required
      />
      <Checkbox
        label="Default locale"
        checked={draft.isDefaultLocale}
        onChange={(event) => onChange({ isDefaultLocale: event.target.checked })}
        hint="The version a subject is shown when their locale has no published version."
        optional
      />
      <Input
        label="Version title"
        value={draft.versionTitle}
        onChange={(event) => onChange({ versionTitle: event.target.value })}
        hint="Names this version in the version history."
        required
      />
      <Textarea
        label="Content"
        value={draft.content}
        onChange={(event) => onChange({ content: event.target.value })}
        autoResize
        hint="The exact text the subject will read and be bound by. A version is immutable once published."
        required
      />
      <Input
        label="Effective from"
        type="date"
        value={draft.effectiveAt}
        onChange={(event) => onChange({ effectiveAt: event.target.value })}
        hint="When this version becomes binding."
        optional
      />
    </div>
  );
}
